import { z } from 'zod';
import apiClient from '@shared/api/http';
import { toItemArray, toSnakeCaseKeys } from '@shared/api/keys';
import type { DeviceInstanceId, VmId } from '../domain/ids';
import type { Vm, VmWritePayload } from '../domain/vm';

// v1 cuelga las máquinas virtuales de su instancia física
// (`/device-instances/{id}/virtual-machines`) y responde camelCase, convertido aquí a
// `snake_case`. `status` sigue siendo string abierto (unión laxa en domain/vm.ts).
const VmDtoSchema = z.object({
  id: z.string(),
  name: z.string(),
  os_id: z.coerce.string(),
  os_name: z.string().nullish(),
  os_version: z.string().nullish(),
  os_edition: z.string().nullish(),
  os_service_pack: z.string().nullish(),
  os_build_number: z.string().nullish(),
  platform_id: z.coerce.string().nullish(),
  platform_name: z.string().nullish(),
  admin_ip: z.string().nullish(),
  ip_addresses: z.array(z.string()).nullish(),
  status: z.string().nullish(),
  environment: z.string().nullish(),
  cpu_cores: z.number().nullish(),
  vcpu: z.number().nullish(),
  memory_gb: z.number().nullish(),
  storage_gb: z.number().nullish(),
  rperfs: z.number().nullish(),
  client_company_id: z.coerce.string().nullish(),
  device_instance_id: z.coerce.string(),
});

/** Filtro de listado (UC-19): en v1 el `deviceInstanceId` es parte de la ruta, no un query param. */
export interface VmListParams {
  deviceInstanceId?: DeviceInstanceId;
}

function toDomain(dto: z.infer<typeof VmDtoSchema>): Vm {
  return {
    id: dto.id as VmId,
    name: dto.name,
    osId: dto.os_id,
    osName: dto.os_name ?? undefined,
    osVersion: dto.os_version ?? undefined,
    osEdition: dto.os_edition ?? undefined,
    osServicePack: dto.os_service_pack ?? undefined,
    osBuildNumber: dto.os_build_number ?? undefined,
    // platformId/clientCompanyId/cpuCores/vcpu/memoryGb/storageGb/rperfs preservan `null` tal cual
    // (domain/vm.ts los tipa explícitamente `number | null`, a diferencia del resto de opcionales).
    platformId: dto.platform_id,
    platformName: dto.platform_name ?? undefined,
    adminIp: dto.admin_ip ?? undefined,
    // ausente/null ⇒ undefined, nunca `[]` forzado (tasks.md T22).
    ipAddresses: dto.ip_addresses ?? undefined,
    status: dto.status ?? undefined,
    environment: dto.environment ?? undefined,
    cpuCores: dto.cpu_cores,
    vcpu: dto.vcpu,
    memoryGb: dto.memory_gb,
    storageGb: dto.storage_gb,
    rperfs: dto.rperfs,
    clientCompanyId: dto.client_company_id,
    deviceInstanceId: dto.device_instance_id as DeviceInstanceId,
  };
}

function toDto(payload: VmWritePayload) {
  return {
    name: payload.name,
    osId: payload.osId,
    platformId: payload.platformId,
    adminIp: payload.adminIp,
    ipAddresses: payload.ipAddresses,
    status: payload.status,
    environment: payload.environment,
    osBuildNumber: payload.osBuildNumber,
    cpuCores: payload.cpuCores,
    vcpu: payload.vcpu,
    memoryGb: payload.memoryGb,
    storageGb: payload.storageGb,
    rperfs: payload.rperfs,
    clientCompanyId: payload.clientCompanyId,
  };
}

const vmsPath = (deviceInstanceId: DeviceInstanceId) =>
  `/device-instances/${deviceInstanceId}/virtual-machines`;

export const vmRepository = {
  async getAll(params?: VmListParams): Promise<Vm[]> {
    if (params?.deviceInstanceId === undefined) return [];
    const { data } = await apiClient.get(vmsPath(params.deviceInstanceId));
    return z
      .array(VmDtoSchema)
      .parse(toSnakeCaseKeys(toItemArray(data)))
      .map(toDomain);
  },

  async getById(deviceInstanceId: DeviceInstanceId, id: VmId): Promise<Vm | null> {
    const { data } = await apiClient.get(`${vmsPath(deviceInstanceId)}/${id}`);
    if (!data) return null;
    return toDomain(VmDtoSchema.parse(toSnakeCaseKeys(data)));
  },

  async create(payload: VmWritePayload): Promise<Vm> {
    const { data } = await apiClient.post(vmsPath(payload.deviceInstanceId), toDto(payload));
    return toDomain(VmDtoSchema.parse(toSnakeCaseKeys(data)));
  },

  async update(id: VmId, payload: VmWritePayload): Promise<Vm> {
    const { data } = await apiClient.patch(
      `${vmsPath(payload.deviceInstanceId)}/${id}`,
      toDto(payload)
    );
    return toDomain(VmDtoSchema.parse(toSnakeCaseKeys(data)));
  },

  // Deja pasar el error del backend tal cual (UC-20: err.response?.data?.message ||
  // t('vms.updateError')) — ese mapeo a mensaje legible es trabajo de application/.
  async remove(deviceInstanceId: DeviceInstanceId, id: VmId): Promise<void> {
    await apiClient.delete(`${vmsPath(deviceInstanceId)}/${id}`);
  },
};
